import React from 'react';
import { Bot, Contact, MapPin, Mic, Phone } from 'lucide-react';
import { formatMessageTimestamp, guessAudioMime } from './chatUtils';

function getContactName(contact, index) {
  return contact?.name?.formatted_name || contact?.name?.first_name || `Contacto ${index + 1}`;
}

function getContactPhones(contact) {
  return (contact?.phones || [])
    .map(phone => phone.phone || phone.wa_id)
    .filter(Boolean);
}

function MessageBubble({ message, mediaType }) {
  const isOutbound = message.direction === 'outbound';

  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[78%] rounded-2xl px-4 py-3 shadow-sm border ${
          isOutbound
            ? 'bg-[#B1336E] text-white border-[#B1336E] rounded-br-md'
            : 'bg-white text-slate-900 border-slate-200 rounded-bl-md'
        }`}
      >
        {message.ai_generated && (
          <div className="flex items-center gap-1 text-[11px] opacity-80 mb-1">
            <Bot className="w-3 h-3" />
            AI draft sent
          </div>
        )}

        {mediaType === 'contact' ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 font-semibold mb-2">
              <Contact className="w-4 h-4" />
              Contacto compartido
            </div>
            {(message.contact_payload || []).map((contact, index) => {
              const phones = getContactPhones(contact);
              return (
                <div key={`${getContactName(contact, index)}-${index}`} className={`rounded-xl p-3 ${isOutbound ? 'bg-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                  <div className="font-semibold text-sm">{getContactName(contact, index)}</div>
                  {phones.length ? phones.map(phone => {
                    const digits = String(phone).replace(/\D/g, '');
                    const waNumber = digits.startsWith('51') ? digits : `51${digits.slice(-9)}`;
                    return (
                      <a
                        key={phone}
                        href={`https://wa.me/${waNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`mt-2 inline-flex items-center gap-2 text-sm underline ${isOutbound ? 'text-white' : 'text-[#B1336E]'}`}
                      >
                        <Phone className="w-4 h-4" />
                        {phone}
                      </a>
                    );
                  }) : (
                    <div className="mt-1 text-xs opacity-70">Sin telefono</div>
                  )}
                </div>
              );
            })}
            {(!message.contact_payload || !message.contact_payload.length) && (
              <div className="text-sm whitespace-pre-wrap">{message.content}</div>
            )}
          </div>
        ) : mediaType === 'location' ? (
          <div>
            <div className="flex items-center gap-2 font-semibold mb-2">
              <MapPin className="w-4 h-4" />
              Ubicacion compartida
            </div>
            <a
              href={`https://www.google.com/maps?q=${message.latitude},${message.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`${isOutbound ? 'text-white' : 'text-[#B1336E]'} underline text-sm`}
            >
              {message.content || `${message.latitude}, ${message.longitude}`}
            </a>
          </div>
        ) : mediaType === 'audio' ? (
          <div>
            <div className="flex items-center gap-2 font-semibold mb-2 text-sm">
              <Mic className="w-4 h-4" />
              Mensaje de voz
            </div>
            <audio controls preload="metadata" className="w-full mb-2">
              <source src={message.media_url} type={guessAudioMime(message.media_url)} />
              Tu navegador no puede reproducir este audio.
            </audio>
            {message.content && <div className="mt-2 text-sm opacity-90">{message.content}</div>}
          </div>
        ) : mediaType === 'image' ? (
          <div>
            <div className="rounded-xl overflow-hidden mb-2 bg-slate-100">
              <img
                src={message.media_url}
                alt="Imagen enviada"
                className="w-full h-auto block"
                onError={(e) => {
                  console.warn('Image failed to load:', message.media_url);
                  e.target.style.display = 'none';
                }}
              />
            </div>
            {message.content && <div className="text-sm opacity-95">{message.content}</div>}
          </div>
        ) : (
          <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
            {message.content}
          </div>
        )}

        <div className={`flex justify-end mt-1 text-[11px] ${isOutbound ? 'text-white/70' : 'text-slate-400'}`}>
          {formatMessageTimestamp(message.timestamp)}
        </div>
      </div>
    </div>
  );
}

export default MessageBubble;
